import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell
} from 'recharts';

import { questionStatsType, questionType } from '../../constants/types';
import styles from './questionBarChart.module.css';
import variables from '../../styles/variables.json';
import { getTranslation, withLang, langType } from '../../i18n';

const { darkBlue, orange } = variables;

const xAxisKey = 'key';
const yAxisKey = 'percentage';

const [barColor, userValueBarColor] = [darkBlue, orange];

const toPercentage = (val) => `${(val * 100).toFixed(0)}%`;

const constructChartData = (responseOptions, distribution) => responseOptions.map(({ value }) => ({
  [xAxisKey]: Number(value),
  [yAxisKey]: distribution[value] ? distribution[value] : null,
  label: toPercentage(distribution[value])
}));

const chartHeight = 196;

const yDomain = [0, 1];
const yTicks = Array.from({ length: 11 }, (v, k) => k / 10);
const yTickInterval = 4;

const QuestionBarChart = ({ question, stats, lang }) => {
  const { distribution, userNumericalResponseValues } = stats;
  const data = constructChartData(question.responseOptions, distribution);
  const tickTranslationsByValue = question.responseOptions.reduce((acc, option) => {
    acc[option.value] = getTranslation(option.text, lang);
    return acc;
  }, []);

  const getTranslatedOptionText = (key) => tickTranslationsByValue[key] || '';

  const dataEntryToCell = (entry) => (
    <Cell
      fill={userNumericalResponseValues
        && userNumericalResponseValues.includes(entry[xAxisKey]) ? userValueBarColor : barColor}
      key={entry[xAxisKey]}
    />
  );

  return (
    <div className={styles.container}>
      <figure aria-label={getTranslation(question.text)}>
        <div className={styles.chart} aria-hidden>
          <ResponsiveContainer height={chartHeight} width="90%" debounce={300}>
            <BarChart data={data}>
              <XAxis dataKey={xAxisKey} interval={0} />
              <YAxis
                domain={yDomain}
                ticks={yTicks}
                tickFormatter={toPercentage}
                interval={yTickInterval}
              />
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <Bar dataKey={yAxisKey} fill={barColor} label={{ position: 'top', formatter: toPercentage }}>
                {data.map(dataEntryToCell)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </figure>
      <ul className={styles.xAxisLegend}>
        {data.map(({ key }) => (
          <li key={key}>{`${key} = ${getTranslatedOptionText(key)}`}</li>
        ))}
      </ul>
    </div>
  );
};

QuestionBarChart.propTypes = {
  question: questionType.isRequired,
  stats: questionStatsType.isRequired,
  ...langType
};

export default withLang(QuestionBarChart);
